import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { TOPIC_COLOR, TYPE_COLOR, type ColorBy } from "../lib/colorKeys.ts";
import {
  AUDIENCES,
  AUDIENCE_LABELS,
  CONTENT_TYPES,
  TOPICS,
  TOPIC_LABELS,
  type Audience,
  type ContentType,
  type Topic,
} from "../../shared/model.ts";
import { ROLES, ROLE_HINTS, ROLE_LABELS, type Role } from "../../shared/roles.ts";
import { FEATURES } from "../lib/features.ts";
import { DATE_RANGE_LABELS, type DateRange, type Filters } from "../lib/filters.ts";

const TYPE_FILTER_LABELS: Record<ContentType, string> = { blog: "Blogs", announcement: "Press", release_note: "Release notes" };

function Segmented<T extends string>({ legend, name, options, labels, value, onChange }: {
  legend: string; name: string; options: readonly T[]; labels: Record<T, string>; value: T; onChange(v: T): void;
}) {
  const id = useId();
  return (
    <>
    {/* Compact select on small screens; segmented radios from sm up. Only one is displayed. */}
    <label className="flex min-w-0 flex-1 flex-col gap-1 sm:hidden">
      <span className={LABEL}>{legend}</span>
      <select value={value} onChange={(e) => onChange(e.target.value as T)} className="w-full rounded-lg border border-line-strong bg-card px-3 py-2 text-sm font-medium">
        {options.map((o) => <option key={o} value={o}>{labels[o]}</option>)}
      </select>
    </label>
    <div role="radiogroup" aria-labelledby={id} className="hidden min-w-0 flex-col gap-1.5 sm:flex lg:flex-row lg:items-center lg:gap-3">
      <span id={id} className={LABEL}>{legend}</span>
      <div className="inline-flex flex-wrap gap-1 self-start rounded-xl border border-line bg-card p-1">
        {options.map((o) => (
          <label key={o} className="relative">
            <input type="radio" name={name} value={o} checked={value === o} onChange={() => onChange(o)} className="peer sr-only" />
            <span className="block cursor-pointer rounded-xl px-3 py-1.5 text-sm font-medium text-ink-soft transition-colors peer-checked:bg-ink peer-checked:text-on-ink peer-focus-visible:outline-3 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-coral-deep hover:text-ink peer-checked:hover:text-on-ink">
              {labels[o]}
            </span>
          </label>
        ))}
      </div>
    </div>
    </>
  );
}

const LABEL = "text-xs font-semibold uppercase tracking-wide text-ink-soft";

const PILL = "inline-flex min-h-11 items-center gap-2 rounded-lg border border-line-strong bg-card px-3 text-sm font-semibold text-ink hover:bg-sand";

/** Role/persona: picks updates for that role and the summary style it reads. */
function RoleSelect({ value, onChange }: { value: Role | "all"; onChange(r: Role | "all"): void }) {
  return (
    <label className={`${PILL} relative pr-8 focus-within:outline-3 focus-within:outline-offset-2 focus-within:outline-coral-deep`}>
      <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="8" r="3.5" /><path d="M5 20a7 7 0 0 1 14 0" /></svg>
      <span>Role:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as Role | "all")}
        title={value === "all" ? "Updates for every role" : ROLE_HINTS[value]}
        className="absolute inset-0 cursor-pointer appearance-none opacity-0"
      >
        <option value="all">All roles</option>
        {ROLES.map((r) => (
          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
        ))}
      </select>
      <span aria-hidden="true">{value === "all" ? "All roles" : ROLE_LABELS[value]}</span>
      <svg aria-hidden="true" viewBox="0 0 24 24" className="pointer-events-none absolute right-2.5 size-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
    </label>
  );
}

interface Props {
  filters: Filters;
  onChange(patch: Partial<Filters>): void;
  /** Card color key; the topic pills or Type options show the matching dots (the legend). */
  colorBy: ColorBy;
  onColorBy(v: ColorBy): void;
  /** Result count, shown beside the controls. */
  summary: ReactNode;
  view: BrowseView;
  /** Omit to hide the view toggle (Browse is blocks only). */
  onView?(v: BrowseView): void;
}

export function FilterBar({ filters, onChange, colorBy, onColorBy, summary, view, onView }: Props) {
  const active = filters.contentTypes.length + (filters.dateRange !== "any" ? 1 : 0);
  return (
    <div className="space-y-3 px-4 sm:px-6 lg:px-8">
      {FEATURES.audienceFilter && (
        <div className="flex items-end gap-3 sm:flex-wrap sm:items-center sm:gap-x-8 sm:gap-y-3">
          <Segmented<Audience> legend="Audience" name="audience" options={AUDIENCES} labels={AUDIENCE_LABELS} value={filters.audience} onChange={(audience) => onChange({ audience })} />
        </div>
      )}

      {/* Topics as pills; the selected one in the accent wash. */}
      <nav aria-label="Topics" className="border-b border-line pb-3">
        <ul className="scroll-snap-row -mx-4 flex gap-1 overflow-x-auto px-4 [mask-image:linear-gradient(to_right,#000_85%,transparent)] sm:mx-0 sm:flex-wrap sm:px-0 sm:[mask-image:none]">
          {(["all", ...TOPICS] as const).map((t) => {
            const on = filters.topic === t;
            return (
              <li key={t} className="shrink-0">
                <button
                  type="button"
                  aria-pressed={on}
                  onClick={() => onChange({ topic: t as Topic | "all" })}
                  className={`inline-flex min-h-11 items-center gap-1.5 rounded-lg px-3 text-sm transition-colors ${on ? "bg-coral-wash font-semibold text-coral-deep" : "font-medium text-ink hover:bg-sand"}`}
                >
                  {colorBy === "topic" && t !== "all" && <span aria-hidden="true" className="size-2 rounded-full" style={{ backgroundColor: TOPIC_COLOR[t] }} />}
                  {t === "all" ? "All topics" : TOPIC_LABELS[t]}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Who you are, everything else behind Filters, and the count. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <RoleSelect value={filters.role} onChange={(role) => onChange({ role })} />
        <FiltersMenu filters={filters} onChange={onChange} colorBy={colorBy} onColorBy={onColorBy} active={active} />
        <span aria-hidden="true" className="hidden h-6 w-px bg-line-strong sm:block" />
        {summary}
        {onView && <ViewToggle view={view} onView={onView} className="lg:ml-auto" />}
      </div>
    </div>
  );
}

const OPTION = "flex min-h-11 cursor-pointer items-center whitespace-nowrap px-2 text-sm font-medium text-ink-soft has-[:checked]:bg-ink has-[:checked]:text-on-ink has-[:focus-visible]:outline-3 has-[:focus-visible]:-outline-offset-3 has-[:focus-visible]:outline-coral-deep hover:bg-sand has-[:checked]:hover:bg-ink";

/** Type, date and card colors in a popover; closes on Escape or a click outside. */
function FiltersMenu({ filters, onChange, colorBy, onColorBy, active }: {
  filters: Filters; onChange(p: Partial<Filters>): void; colorBy: ColorBy; onColorBy(v: ColorBy): void; active: number;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const typeId = useId();
  const colorId = useId();
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);
  const toggleType = (t: ContentType) =>
    onChange({ contentTypes: filters.contentTypes.includes(t) ? filters.contentTypes.filter((x) => x !== t) : [...filters.contentTypes, t] });

  return (
    <div ref={root} className="relative">
      <button ref={button} type="button" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)} className={PILL}>
        <svg aria-hidden="true" viewBox="0 0 24 24" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"><path d="M4 5h16l-6 7.5V19l-4-2v-4.5L4 5Z" /></svg>
        Filters
        {active > 0 && <span className="rounded-full bg-ink px-1.5 text-2xs font-semibold leading-5 text-on-ink">{active}<span className="sr-only"> active</span></span>}
      </button>
      {open && (
        <div id={panelId} role="group" aria-label="Filters" className="absolute left-0 top-full z-30 mt-2 w-[min(24rem,calc(100vw-2rem))] space-y-4 rounded-xl border border-line bg-card p-4 shadow-lg">
          <div role="group" aria-labelledby={typeId} className="space-y-1.5">
            <span id={typeId} className={LABEL}>Type</span>
            <div className="flex overflow-hidden rounded-lg border border-line-strong">
              {CONTENT_TYPES.map((t, i) => (
                <label key={t} className={`${OPTION} flex-1 justify-center ${i ? "border-l border-line-strong" : ""}`}>
                  <input type="checkbox" className="sr-only" checked={filters.contentTypes.includes(t)} onChange={() => toggleType(t)} />
                  {colorBy === "type" && <span aria-hidden="true" className="mr-1.5 size-2 rounded-full ring-1 ring-white/70" style={{ backgroundColor: TYPE_COLOR[t] }} />}
                  {TYPE_FILTER_LABELS[t]}
                </label>
              ))}
            </div>
          </div>
          <label className="block space-y-1.5">
            <span className={LABEL}>Date</span>
            <select value={filters.dateRange} onChange={(e) => onChange({ dateRange: e.target.value as DateRange })} className="block min-h-11 w-full rounded-lg border border-line-strong bg-card py-2 pl-3 pr-8 text-sm font-medium">
              {(Object.keys(DATE_RANGE_LABELS) as DateRange[]).map((d) => (
                <option key={d} value={d}>{DATE_RANGE_LABELS[d]}</option>
              ))}
            </select>
          </label>
          <div role="radiogroup" aria-labelledby={colorId} className="space-y-1.5">
            <span id={colorId} className={LABEL}>Color cards by</span>
            <div className="flex overflow-hidden rounded-lg border border-line-strong">
              {(["topic", "type", "off"] as const).map((v, i) => (
                <label key={v} className={`${OPTION} flex-1 justify-center ${i ? "border-l border-line-strong" : ""}`}>
                  <input type="radio" name="color-by" className="sr-only" checked={colorBy === v} onChange={() => onColorBy(v)} />
                  {v === "topic" ? "Topic" : v === "type" ? "Type" : "Off"}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export type BrowseView = "grid" | "carousel" | "list";

function ViewToggle({ view, onView, className = "" }: { view: BrowseView; onView(v: BrowseView): void; className?: string }) {
  return (
    <div role="group" aria-label="View" className={`inline-flex rounded-lg border border-line bg-card p-1 ${className}`}>
      {(["grid", "carousel", "list"] as const).map((v) => (
        <button key={v} type="button" aria-pressed={view === v} onClick={() => onView(v)} className={`rounded-lg px-3 py-1 text-sm font-medium ${view === v ? "bg-ink text-on-ink" : "text-ink-soft hover:text-ink"}`}>
          {v === "grid" ? "Grid" : v === "carousel" ? "Carousel" : "List"}
        </button>
      ))}
    </div>
  );
}
